/**
 * StoreLocationOffersLoader — fetches one selected store's offers and reports
 * them up (T12.1.5.1.2).
 *
 * Why a component, not a loop: `useRetailerOffers(storeId)` is one query per
 * store and React forbids calling hooks in a loop. So the Planning row mounts
 * one tiny always-rendered loader per selected store; each owns a single
 * `useRetailerOffers` call and pushes its resolved `StoreLocationOffers` up
 * through `onResolved`. The loader renders nothing — the row aggregates the
 * reported offers to derive the cross-store status chip AND to feed the expanded
 * per-store availability list. TanStack caches per store id, so several rows
 * mounting loaders for the same stores share one fetch.
 */

import { useEffect } from 'react';
import { useRetailerOffers } from '@shopista/shared/api/hooks';
import type { StoreLocationOffers } from './storeLocationOfferResolver';

export interface StoreLocationOffersLoaderProps {
  storeId: string;
  storeName: string | null;
  onResolved: (result: StoreLocationOffers) => void;
}

export function StoreLocationOffersLoader({
  storeId,
  storeName,
  onResolved,
}: StoreLocationOffersLoaderProps): null {
  const query = useRetailerOffers(storeId);
  const offers = query.data?.offers;
  const loading = query.isLoading;

  useEffect(() => {
    onResolved({
      storeId,
      storeName,
      offers: offers ?? [],
      loading,
    });
    // `offers` is a stable TanStack reference per fetch; reporting on its change
    // (and on the loading flag) keeps the parent's aggregate current without an
    // infinite loop. `onResolved` is memoized by the parent.
  }, [storeId, storeName, offers, loading, onResolved]);

  return null;
}
